import * as React from "react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { FormProduct, FormVariant } from "@shared/types";
import { Alert, AlertDescription } from "@bcc/components/ui/alert";
import { Button } from "@bcc/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { SingleVariantForm } from "../single-variant-form";
import { RequiredVariantHint } from "../required-variant-hint";
import { getBagSizeGroups, grindsByBagSize, nextSunday, setAvailableLocalisedTermOptions } from "../utils";
import { REQUIRED_GRIND } from "../constants";
import type { BagSizeGroup } from "../types";
import type { VariantFormProps } from "./variant-form.types";
import { getOtherRowsBagSizes } from "./variant-form.utils";

/**
 * VariantForm - Manages the variant array via useFieldArray
 *
 * This component orchestrates the variant list, computing derived state
 * (bag size groups, grinds by bag size, selected count) and passing context
 * to each SingleVariantForm instance.
 */
export const VariantForm = ({
  productConfig,
  localisedProductConfig,
  isSingleBagSizeEnabled = false,
  isProductImportedLocal,
  shortTermMaxWeeklyInventory,
  shortTermMinWeeklyInventory,
  longTermMinWeeklyInventory,
  longTermMaxWeeklyInventory,
  regionLargeBagSize,
  allowAddRemoveVariants = false,
  excludedBagSizes,
  homeRegion,
  taxRatesByRegion,
  shipToOptions,
}: VariantFormProps) => {
  const { t } = useTranslation();
  const form = useFormContext<FormProduct>();

  // Setup useFieldArray for variants
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "variants",
  });

  const handleAddVariant = React.useCallback(() => {
    append({
      type: "variant",
      id: `ss-variant-${globalThis.crypto.randomUUID().slice(0, 8)}`,
      bagSize: "",
      grind: [],
      price: "",
      wholesalePrice: "",
      term: "Long",
      startDate: "",
      isProductSelectedForImport: true,
    } as FormVariant);
  }, [append]);

  const handleRemoveVariant = React.useCallback(
    (index: number) => {
      if (fields.length > 1) {
        remove(index);
      }
    },
    [fields.length, remove]
  );

  // Watch all variants for derived state computation
  const variants = form.watch("variants");
  const localVariants = form.watch("localVariants");

  // ===== DERIVED STATE COMPUTATION (from client useVariantForm hook) =====

  // 1. Selected variant count
  const selectedVariantCount = variants?.filter((variant) => variant.isProductSelectedForImport).length ?? 0;

  // 2. Next available start day (next Sunday)
  const nextAvailableStartDay = React.useMemo(() => nextSunday(), []);

  // 3. Grinds by bag size Map
  const grindsByBagSizeMap = React.useMemo(() => {
    if (!variants) return new Map();
    return grindsByBagSize(variants);
  }, [variants]);

  // 4. Previous imported bag sizes (based on import state)
  const previousImportedBagSizes = React.useMemo(() => {
    const product = form.getValues();

    if (isProductImportedLocal && isSingleBagSizeEnabled) {
      return product.localVariants?.map((variant) => variant.bagSize)[0];
    }

    if (isProductImportedLocal) {
      return product.localVariants?.map((variant) => variant.bagSize);
    }

    if (isSingleBagSizeEnabled) {
      return variants?.find((variant) => variant.isImportedRemote)?.bagSize ?? undefined;
    }

    return [
      ...new Set(
        variants?.filter((variant) => variant.isImportedRemote && variant.status !== "Rejected")?.map((variant) => variant.bagSize) ?? []
      ),
    ];
  }, [isProductImportedLocal, isSingleBagSizeEnabled, form, variants]);

  // 5. Single bag size (if single bag size mode enabled)
  const [singleBagSize, setSingleBagSize] = React.useState<string | undefined>(
    Array.isArray(previousImportedBagSizes) ? undefined : previousImportedBagSizes
  );

  React.useEffect(() => {
    if (!isSingleBagSizeEnabled || previousImportedBagSizes) {
      return;
    }

    const selectedVariant = variants?.find((variant) => variant.isProductSelectedForImport);
    const resolvedBagSize = selectedVariant?.mapDetectedBagSizeTo ?? selectedVariant?.bagSize;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync derived single bag size when import selection or available bag sizes change
    setSingleBagSize((currentBagSize) => (currentBagSize === resolvedBagSize ? currentBagSize : resolvedBagSize));
  }, [isSingleBagSizeEnabled, previousImportedBagSizes, variants]);

  // 6. Bag size groups (selected + remote, and all imported)
  const { allImportedGroups } = React.useMemo(() => {
    const product = form.getValues();
    return getBagSizeGroups({
      ...product,
      variants: variants ?? [],
      localVariants,
    });
  }, [form, localVariants, variants]);

  // 7. Bag size groups with required grind (for RequiredVariantHint)
  const bagSizeGroupsWithRequiredGrindType = React.useMemo(() => {
    return allImportedGroups.filter((group: BagSizeGroup) => {
      // Only include groups that have selected variants
      const hasSelectedVariant = variants?.some(
        (variant) => variant.isProductSelectedForImport && (variant.mapDetectedBagSizeTo ?? variant.bagSize) === group.bagSize
      );
      return hasSelectedVariant;
    });
  }, [allImportedGroups, variants]);

  // ===== HELPER FUNCTIONS =====

  // Filter bag size groups for a specific variant
  const filterBagSizeGroups = React.useCallback((bagSizeGroups: BagSizeGroup[], bagSize: string, mapDetectedBagSizeTo?: string) => {
    const bagSizeToQuery = mapDetectedBagSizeTo ?? bagSize;
    return bagSizeGroups.find((group) => group.bagSize === bagSizeToQuery);
  }, []);

  // Check if variant's bag size group has required grind
  const getIsVariantBagSizeGroupHasRequiredGrind = React.useCallback(
    (variant: FormVariant) => {
      const bagSizeGrinds = grindsByBagSizeMap?.get(variant.mapDetectedBagSizeTo ?? variant.bagSize) ?? [];
      return bagSizeGrinds.includes(REQUIRED_GRIND);
    },
    [grindsByBagSizeMap]
  );

  // Check if detected grind override is enabled for variant
  const isDetectedGrindOverrideEnabled = React.useCallback((variant: FormVariant) => {
    const grinds = variant.detectedGrindInfo;
    if (!Array.isArray(grinds) || grinds.length === 0) return false;
    if (grinds.length === 1 && grinds[0]?.grind === REQUIRED_GRIND) {
      return false;
    }
    return true;
  }, []);

  // Localized term options filtered by product config
  const localisedTermOptions = React.useMemo(() => {
    return setAvailableLocalisedTermOptions(localisedProductConfig.grind, productConfig.grind);
  }, [localisedProductConfig.grind, productConfig.grind]);

  // Display bag sizes string for notices
  const displayBagSizes = React.useMemo(() => {
    if (previousImportedBagSizes && !Array.isArray(previousImportedBagSizes)) {
      return previousImportedBagSizes;
    }
    if (previousImportedBagSizes && Array.isArray(previousImportedBagSizes)) {
      if (previousImportedBagSizes.length === 1) {
        return previousImportedBagSizes[0];
      }
      return previousImportedBagSizes.join(", ");
    }
    return undefined;
  }, [previousImportedBagSizes]);

  const showPreviousImportedBagSizes = React.useMemo(() => {
    return Array.isArray(previousImportedBagSizes) && previousImportedBagSizes.length > 0;
  }, [previousImportedBagSizes]);

  // Determine notice type based on validation state
  const noticeType = React.useMemo(() => {
    if (selectedVariantCount === 0) return "default";

    if (allImportedGroups.some((bagSizeGroup: BagSizeGroup) => !bagSizeGroup.grindTypes.includes(REQUIRED_GRIND))) {
      return "destructive";
    }
    return "default";
  }, [allImportedGroups, selectedVariantCount]);

  // ===== RENDERING =====

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div>
        {selectedVariantCount > 0 && (
          <p className="text-sm text-muted-foreground">
            {selectedVariantCount} {selectedVariantCount === 1 ? "variant" : "variants"} selected
          </p>
        )}
      </div>

      {/* Notice blocks with RequiredVariantHint */}
      {isSingleBagSizeEnabled && (
        <Alert variant={noticeType}>
          <AlertDescription>
            <p className="mb-2">
              {t("components.VariantForm.variantsSingleBagHint", {
                defaultValue: "Single bag size mode enabled",
              })}
            </p>
            {displayBagSizes && (
              <p className="mb-2">
                {t("components.VariantForm.variantFormImportedBagSizesMessage", {
                  size: displayBagSizes,
                  defaultValue: `Previously imported bag size: ${displayBagSizes}`,
                })}
              </p>
            )}
            <RequiredVariantHint
              bagSizeGroupsWithRequiredGrindType={bagSizeGroupsWithRequiredGrindType}
              requiredGrind={REQUIRED_GRIND}
              grindConfig={localisedProductConfig.grind}
            />
          </AlertDescription>
        </Alert>
      )}

      {!isSingleBagSizeEnabled && showPreviousImportedBagSizes && (
        <Alert variant={noticeType}>
          <AlertDescription>
            <p className="mb-2">
              {t("components.VariantForm.variantsImportedHint", {
                defaultValue: "Previously imported variants detected",
              })}
            </p>
            {displayBagSizes && (
              <p className="mb-2">
                {t("components.VariantForm.variantFormImportedBagSizesMessage", {
                  size: displayBagSizes,
                  defaultValue: `Previously imported bag sizes: ${displayBagSizes}`,
                })}
              </p>
            )}
            <RequiredVariantHint
              bagSizeGroupsWithRequiredGrindType={bagSizeGroupsWithRequiredGrindType}
              requiredGrind={REQUIRED_GRIND}
              grindConfig={localisedProductConfig.grind}
            />
          </AlertDescription>
        </Alert>
      )}

      {!isSingleBagSizeEnabled && !showPreviousImportedBagSizes && (
        <Alert variant={noticeType}>
          <AlertDescription>
            <RequiredVariantHint
              bagSizeGroupsWithRequiredGrindType={bagSizeGroupsWithRequiredGrindType}
              requiredGrind={REQUIRED_GRIND}
              grindConfig={localisedProductConfig.grind}
            />
          </AlertDescription>
        </Alert>
      )}

      {/* No variants selected warning */}
      {selectedVariantCount === 0 && (
        <Alert variant="destructive">
          <AlertDescription>
            {t("components.VariantForm.noVariantsSelected", {
              defaultValue: "Please select at least one variant for import",
            })}
          </AlertDescription>
        </Alert>
      )}

      {/* Render SingleVariantForm for each variant */}
      <div className="space-y-4">
        {fields.map((field, index) => {
          const variant = variants?.[index];
          if (!variant) return null;

          // Merge already-imported sizes with sizes chosen on sibling rows so the same bag
          // size cannot be added twice in one request. The row's own selection is preserved
          // because SingleVariantForm keeps `variant.bagSize` visible regardless.
          const rowExcludedBagSizes = [...(excludedBagSizes ?? []), ...getOtherRowsBagSizes(variants, index)];

          return (
            <div key={field.id} className="relative">
              <SingleVariantForm
                index={index}
                nextAvailableStartDay={nextAvailableStartDay}
                productConfig={productConfig}
                selectedVariantCount={selectedVariantCount}
                isProductImportedLocal={isProductImportedLocal}
                singleBagSize={singleBagSize}
                termConfig={localisedProductConfig.term}
                grindConfig={localisedTermOptions}
                bagSizeGroup={filterBagSizeGroups(allImportedGroups, variant.bagSize, variant.mapDetectedBagSizeTo)}
                isOverrideGrindEnabled={!getIsVariantBagSizeGroupHasRequiredGrind(variant) || isDetectedGrindOverrideEnabled(variant)}
                shortTermMaxWeeklyInventory={shortTermMaxWeeklyInventory}
                shortTermMinWeeklyInventory={shortTermMinWeeklyInventory}
                longTermMinWeeklyInventory={longTermMinWeeklyInventory}
                longTermMaxWeeklyInventory={longTermMaxWeeklyInventory}
                regionLargeBagSize={regionLargeBagSize}
                excludedBagSizes={rowExcludedBagSizes}
                homeRegion={homeRegion}
                taxRatesByRegion={taxRatesByRegion}
                shipToOptions={shipToOptions}
              />
              {allowAddRemoveVariants && fields.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 text-destructive hover:text-destructive"
                  onClick={() => handleRemoveVariant(index)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  {t("common.remove", { defaultValue: "Remove" })}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {allowAddRemoveVariants && (
        <Button type="button" variant="outline" size="sm" onClick={handleAddVariant}>
          <Plus className="h-4 w-4 mr-1" />
          {t("components.VariantForm.addVariant", {
            defaultValue: "Add Bag Size",
          })}
        </Button>
      )}
    </div>
  );
};
